import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { insuranceTypes, partnerInsurers, COMPANY } from "../data/insurances";

const howItWorksSteps = [
  {
    n: "01",
    title: "Preencha os dados",
    desc: "Conte o que você quer proteger. Cotação rápida, sem compromisso.",
    icon: "fa-file-pen",
  },
  {
    n: "02",
    title: "Comparamos para você",
    desc: "Nossa equipe cota nas melhores seguradoras parceiras e traz a opção ideal.",
    icon: "fa-scale-balanced",
  },
  {
    n: "03",
    title: "Contrate com cuidado humano",
    desc: "Um consultor especialista explica cada cobertura e acompanha do início ao sinistro.",
    icon: "fa-handshake-angle",
  },
];

const testimonials = [
  {
    name: "Carlos Mendes",
    role: "Cliente desde 2023",
    quote:
      "Já tive outros seguros, mas a Safe Life é diferente. Me trataram pelo nome e explicaram cada detalhe — não é só preço, é confiança.",
    initials: "CM",
  },
  {
    name: "Juliana Ribeiro",
    role: "Empresária, Jales/SP",
    quote:
      "Contratei o seguro empresarial e o residencial. A Camila, minha consultora, é fantástica. Resolveu um sinistro meu em 24h.",
    initials: "JR",
  },
  {
    name: "Rafael Tavares",
    role: "Motorista de aplicativo",
    quote:
      "Achei que seguro era caro demais. A Safe Life me mostrou uma opção mensal que cabe no meu bolso. Recomendo!",
    initials: "RT",
  },
];

const faqs = [
  {
    q: "A Safe Life é uma seguradora?",
    a: "Não. A Safe Life é uma corretora de seguros devidamente registrada na SUSEP (código 261177097). Trabalhamos com as melhores seguradoras do mercado (Porto Seguro, Tokio Marine, Grupo HDI, Allianz, Suhai e Ituran) e comparamos opções para encontrar o seguro ideal para você.",
  },
  {
    q: "Quanto tempo leva para receber minha cotação?",
    a: "Trabalhamos com retorno rápido em horário comercial. Assim que você envia o formulário, um consultor entra em contato pelo WhatsApp, telefone ou e-mail — como você preferir.",
  },
  {
    q: "Vocês atendem em todo o Brasil?",
    a: "Sim. Apesar de estarmos sediados em Jales/SP, atendemos clientes em todo o Brasil de forma digital. Nossas seguradoras parceiras oferecem cobertura nacional e, em alguns casos, cobertura para o Mercosul.",
  },
  {
    q: "Preciso pagar alguma taxa para fazer uma cotação?",
    a: "Não. A cotação é totalmente gratuita e sem compromisso. Você só paga o seguro que efetivamente contratar.",
  },
  {
    q: "Como vocês cuidam dos meus dados pessoais?",
    a: "Seguimos rigorosamente a LGPD (Lei Geral de Proteção de Dados). Seus dados são usados exclusivamente para gerar cotações e prestar atendimento, e você pode solicitar exclusão a qualquer momento em contato@safelifeseguros.com.br.",
  },
  {
    q: "E se eu precisar acionar o seguro?",
    a: "Nós estamos com você do início ao fim. Em caso de sinistro, nossa equipe acompanha todo o processo junto à seguradora para garantir que você receba a indenização de forma rápida e transparente.",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-white to-navy-50 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="absolute inset-0 brick-grid-bg opacity-60" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-navy-50 text-navy px-4 py-2 rounded-full text-xs font-semibold mb-6">
                <span className="w-2 h-2 bg-lego rounded-full animate-pulse" />
                Corretora registrada SUSEP {COMPANY.susep}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy leading-[1.05] tracking-tight">
                Proteger a vida é <br />
                <span className="relative inline-block">
                  <span className="font-serif-accent italic text-lego">algo pessoal.</span>
                  <svg
                    className="absolute -bottom-3 left-0 w-full"
                    viewBox="0 0 200 8"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0 4 Q 50 0 100 4 T 200 4"
                      stroke="#DC2626"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </h1>
              <p className="mt-8 text-lg text-navy/70 leading-relaxed max-w-2xl">
                Somos uma corretora que enxerga além da apólice. Comparamos, explicamos e cuidamos — do início da cotação ao sinistro. Com as melhores seguradoras do Brasil.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full font-bold h-14 px-8 shadow-soft-lg hover:-translate-y-1 transition-all"
                  data-testid="hero-cta-cotar"
                >
                  <Link to="/cotacao">
                    <i className="fa-solid fa-shield-heart mr-2" />
                    Cotar grátis
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="rounded-full font-semibold h-14 px-8 border-2 border-navy text-navy hover:bg-navy hover:text-white"
                  data-testid="hero-cta-whatsapp"
                >
                  <a
                    href={`https://wa.me/55${COMPANY.whatsapp}?text=${encodeURIComponent(
                      "Olá! Quero falar com um especialista da Safe Life."
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i className="fa-brands fa-whatsapp mr-2 text-xl" />
                    Falar no WhatsApp
                  </a>
                </Button>
              </div>

              <p className="mt-6 text-xs text-navy/50 font-medium">
                <i className="fa-solid fa-lock mr-1" /> Dados protegidos · Sem compromisso · LGPD compliant
              </p>
            </div>

            {/* Hero visual — LEGO heart story card */}
            <div className="lg:col-span-5 relative">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-32 h-32 bg-lego/10 rounded-3xl rotate-12 brick-pattern" />
                <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-navy/5 rounded-3xl -rotate-6" />
                <div className="relative bg-white rounded-3xl p-8 shadow-soft-lg border border-navy-100">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-lego/10 rounded-2xl flex items-center justify-center">
                      <i className="fa-solid fa-heart text-lego text-2xl animate-pulse" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-navy/50 font-bold">Nossa essência</p>
                      <p className="font-heading font-bold text-navy text-lg">Construído com amor.</p>
                    </div>
                  </div>
                  <p className="font-serif-accent italic text-navy/80 leading-relaxed">
                    "Nosso símbolo nasceu dentro de casa. Um coração construído com LEGO pelo filho de um dos sócios. Porque para nós, proteger a vida é algo pessoal."
                  </p>

                  <div className="mt-6 pt-6 border-t border-navy-100 grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-heading font-extrabold text-navy">+6</p>
                      <p className="text-[10px] uppercase tracking-wider text-navy/60">seguradoras parceiras</p>
                    </div>
                    <div>
                      <p className="text-lg md:text-xl font-heading font-extrabold text-navy leading-tight">Retorno rápido</p>
                      <p className="text-[10px] uppercase tracking-wider text-navy/60">cotação rápida</p>
                    </div>
                    <div>
                      <p className="text-2xl font-heading font-extrabold text-navy">100%</p>
                      <p className="text-[10px] uppercase tracking-wider text-navy/60">atendimento humano</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-navy text-white py-6" data-testid="trust-bar">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-xs md:text-sm">
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-shield-halved text-lego" />
              <strong>SUSEP</strong> {COMPANY.susep}
            </span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-building text-lego" />
              <strong>CNPJ</strong> {COMPANY.cnpj}
            </span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-headset text-lego" />
              Atendimento humano
            </span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-handshake text-lego" />
              Suporte até o sinistro
            </span>
            <span className="flex items-center gap-2">
              <i className="fa-solid fa-lock text-lego" />
              LGPD compliant
            </span>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 md:py-28" data-testid="products-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-2xl mb-14">
            <p className="text-lego text-sm font-bold uppercase tracking-widest mb-3">Nossos seguros</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy tracking-tight">
              Tudo o que importa para você —<br />
              <span className="font-serif-accent italic text-navy/70">protegido por quem entende.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {insuranceTypes.map((ins, idx) => (
              <Link
                key={ins.slug}
                to={`/seguros/${ins.slug}`}
                className="group bg-white rounded-3xl p-8 border border-navy-100 hover:border-lego hover:shadow-soft-lg transition-all duration-300"
                data-testid={`product-card-${ins.slug}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 bg-navy-50 group-hover:bg-lego/10 rounded-2xl flex items-center justify-center transition-colors">
                    <i className={`fa-solid ${ins.icon} text-navy group-hover:text-lego text-xl transition-colors`} />
                  </div>
                  <span className="text-xs font-mono text-navy/40 font-bold">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold text-navy mb-2">{ins.name}</h3>
                <p className="text-sm text-navy/70 leading-relaxed mb-5">{ins.tagline}</p>
                <span className="inline-flex items-center gap-2 text-lego font-semibold text-sm group-hover:gap-3 transition-all">
                  Saiba mais
                  <i className="fa-solid fa-arrow-right text-xs" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 bg-surface" data-testid="how-it-works-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-lego text-sm font-bold uppercase tracking-widest mb-3">Como funciona</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy">
              Simples como montar blocos.
            </h2>
            <p className="mt-4 text-navy/70">Três passos para ter o seguro certo na sua vida.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {howItWorksSteps.map((s, i) => (
              <div key={s.n} className="relative" data-testid={`step-${i + 1}`}>
                <div className="bg-white rounded-3xl p-8 h-full shadow-soft border border-navy-100">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-14 h-14 bg-lego/10 rounded-2xl flex items-center justify-center">
                      <i className={`fa-solid ${s.icon} text-lego text-xl`} />
                    </div>
                    <span className="font-heading text-5xl font-extrabold text-navy/10">{s.n}</span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-navy mb-2">{s.title}</h3>
                  <p className="text-sm text-navy/70 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY — emotional LEGO section */}
      <section className="py-20 md:py-32 bg-navy text-white relative overflow-hidden" data-testid="our-story-section">
        <div className="absolute inset-0 brick-pattern opacity-40" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-lego text-sm font-bold uppercase tracking-widest mb-4">Nossa história</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
                Um coração de LEGO.<br />
                <span className="font-serif-accent italic text-white/70">Uma promessa de verdade.</span>
              </h2>
              <div className="mt-8 space-y-5 text-white/80 leading-relaxed text-lg">
                <p>
                  Nosso símbolo nasceu dentro de casa. Um coração construído com <strong className="text-lego">LEGO</strong> pelo filho de um dos sócios — pequenas peças, encaixe firme, amor puro.
                </p>
                <p>
                  Porque para nós, proteger a vida é <em>algo pessoal</em>. Cada apólice que entregamos é uma pedra na construção de um futuro mais tranquilo para uma família, uma empresa, um sonho.
                </p>
                <p>
                  É por isso que combinamos tecnologia com gente de verdade. Comparamos as melhores seguradoras, explicamos cada cobertura e ficamos com você quando o imprevisto bate à porta.
                </p>
              </div>
              <Button
                asChild
                size="lg"
                className="mt-10 bg-lego hover:bg-lego/90 text-white rounded-full font-bold h-14 px-8"
                data-testid="story-cta"
              >
                <Link to="/sobre">
                  Conheça a Safe Life
                  <i className="fa-solid fa-arrow-right ml-2" />
                </Link>
              </Button>
            </div>

            <div className="lg:col-span-5">
              {/* Stylized LEGO heart */}
              <div className="relative mx-auto w-80 h-80">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    {/* heart composed of squares */}
                    <div className="grid grid-cols-7 gap-1 w-64">
                      {[
                        0,1,1,0,1,1,0,
                        1,1,1,1,1,1,1,
                        1,1,1,1,1,1,1,
                        0,1,1,1,1,1,0,
                        0,0,1,1,1,0,0,
                        0,0,0,1,0,0,0,
                      ].map((c, i) => (
                        <div
                          key={i}
                          className={`aspect-square rounded-sm ${
                            c ? "bg-lego shadow-soft" : "bg-transparent"
                          }`}
                          style={{
                            boxShadow: c
                              ? "inset 0 4px 0 rgba(255,255,255,0.2), inset 0 -4px 0 rgba(0,0,0,0.15)"
                              : "none",
                          }}
                        />
                      ))}
                    </div>
                    {/* LEGO studs */}
                    <div className="absolute inset-0 grid grid-cols-7 gap-1 w-64 pointer-events-none">
                      {[
                        0,1,1,0,1,1,0,
                        1,1,1,1,1,1,1,
                        1,1,1,1,1,1,1,
                        0,1,1,1,1,1,0,
                        0,0,1,1,1,0,0,
                        0,0,0,1,0,0,0,
                      ].map((c, i) => (
                        <div key={i} className="aspect-square flex items-center justify-center">
                          {c === 1 && (
                            <div className="w-1/2 h-1/2 rounded-full bg-black/10" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-2xl animate-float" />
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-lego/20 rounded-2xl animate-float" style={{ animationDelay: "2s" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-16 md:py-20 bg-white" data-testid="partners-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-10">
            <p className="text-brand text-sm font-bold uppercase tracking-widest mb-3">Seguradoras parceiras</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-navy">
              Comparamos nas melhores. Você escolhe a ideal.
            </h2>
          </div>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5"
            data-testid="partners-grid"
          >
            {partnerInsurers.map((p) => (
              <div
                key={p.name}
                className="bg-white rounded-2xl border border-navy-100 hover:border-brand/40 hover:shadow-soft transition-all p-5 flex flex-col items-center justify-center gap-3 min-h-[140px]"
                data-testid={`partner-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="h-14 w-full flex items-center justify-center">
                  <img
                    src={p.logoUrl}
                    alt={`Logo ${p.name}`}
                    className="max-h-12 max-w-full object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      const fallback = e.currentTarget.nextElementSibling;
                      if (fallback) fallback.style.display = "flex";
                    }}
                  />
                  <div
                    className="hidden w-12 h-12 rounded-xl items-center justify-center text-white font-heading font-bold text-lg"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.name.charAt(0)}
                  </div>
                </div>
                <p className="font-heading font-bold text-navy text-sm text-center leading-tight">
                  {p.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 bg-surface" data-testid="testimonials-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-2xl mb-14">
            <p className="text-lego text-sm font-bold uppercase tracking-widest mb-3">Quem confia na gente</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy">
              Pessoas reais. Histórias reais.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className="bg-white rounded-3xl p-8 border border-navy-100 shadow-soft"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 mb-5 text-lego">
                  {[...Array(5)].map((_, j) => (
                    <i key={j} className="fa-solid fa-star text-sm" />
                  ))}
                </div>
                <p className="text-navy/80 leading-relaxed mb-6 font-serif-accent italic">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-navy-100">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center text-white font-bold font-heading">
                    {t.initials}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-navy">{t.name}</p>
                    <p className="text-xs text-navy/60">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28" data-testid="faq-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <div className="text-center mb-14">
            <p className="text-lego text-sm font-bold uppercase tracking-widest mb-3">Perguntas frequentes</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy">
              Tudo o que você precisa saber.
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white border border-navy-100 rounded-2xl px-6 data-[state=open]:border-lego/40 data-[state=open]:shadow-soft"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left font-heading font-bold text-navy hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-navy/70 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-navy relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 brick-pattern opacity-30" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              Pronto para proteger o <br />
              <span className="font-serif-accent italic text-lego">que importa de verdade?</span>
            </h2>
            <p className="mt-6 text-white/80 text-lg">
              Solicite sua cotação grátis. Sem compromisso. Sem cobrança.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-brand hover:bg-brand-600 active:bg-brand-700 text-white rounded-full font-bold h-14 px-8 shadow-soft-lg hover:-translate-y-1 transition-all"
                data-testid="final-cta-cotar"
              >
                <Link to="/cotacao">
                  <i className="fa-solid fa-shield-heart mr-2" />
                  Cotar meu seguro agora
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full font-semibold h-14 px-8 border-2 border-white/30 bg-white/5 text-white hover:bg-white hover:text-navy"
                data-testid="final-cta-whatsapp"
              >
                <a
                  href={`https://wa.me/55${COMPANY.whatsapp}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa-brands fa-whatsapp mr-2 text-xl" />
                  WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
